import { useEffect, useRef, useCallback } from 'react';
import { refreshAccessToken } from '../services/authService';
import { universalAuthService, UserType } from '../services/universalAuthService';

// Функция для декодирования JWT токена
const decodeJwtToken = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    return JSON.parse(atob(parts[1]));
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Хук для автоматического обновления токенов
// userType: 'user' | 'field' | 'referee' - тип пользователя для обновления
export const useTokenRefresh = (userType: UserType = 'user') => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  const checkAndRefreshToken = useCallback(async () => {
    // Предотвращаем параллельные запросы refresh
    if (isRefreshingRef.current) {
      return;
    }

    const { token, refreshToken } = universalAuthService.getStoredData(userType);

    if (!token || !refreshToken) {
      return;
    }

    try {
      const payload = decodeJwtToken(token);
      if (!payload || !payload.exp) {
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - now;

      // Обновляем токен за 5 минут до истечения ИЛИ если уже истек
      // Убрано условие `timeUntilExpiry > 0` чтобы обрабатывать истекшие токены
      if (timeUntilExpiry < 300) {
        console.log(`[${userType}] Token ${timeUntilExpiry < 0 ? 'expired' : 'expires soon'}, attempting refresh...`);

        isRefreshingRef.current = true;

        try {
          // Передаём userType в refreshAccessToken
          const newTokens = await refreshAccessToken(userType);
          if (newTokens) {
            console.log(`[${userType}] Token refresh successful`);
            universalAuthService.updateTokens(userType, newTokens.token, newTokens.refreshToken);
          } else {
            console.log(`[${userType}] Token refresh failed`);
          }
        } finally {
          isRefreshingRef.current = false;
        }
      }
    } catch (error) {
      console.error(`[${userType}] Error in token refresh check:`, error);
      isRefreshingRef.current = false;
    }
  }, [userType]);

  useEffect(() => {
    // Проверяем сразу при монтировании
    checkAndRefreshToken();

    // Проверяем токен каждую минуту (60000 мс)
    intervalRef.current = setInterval(checkAndRefreshToken, 60000);

    // Обработка возвращения к вкладке
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log(`[${userType}] Tab became visible, checking token...`);
        checkAndRefreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Очистка при размонтировании
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndRefreshToken, userType]);

  // Возвращаем функцию для ручного обновления
  return {
    manualRefresh: async () => {
      const newTokens = await refreshAccessToken(userType);
      if (newTokens) {
        universalAuthService.updateTokens(userType, newTokens.token, newTokens.refreshToken);
        return true;
      }
      return false;
    }
  };
};